/* eslint-disable max-lines */
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useHistory} from "react-router"
import {StoreHelpers} from "react-joyride";
import ReactTour, {TourStep} from "../../components/ReactTour";
import {queryData} from "./project-edit.graphql";
import {
  createDummyProject,
  createDummyChallenge,
} from "../../dummies";
import {getLanguageCode} from "../../i18n";
import {useTour} from "../../providers/tour";
import {
  getTargetsClassNamesWithPrefix,
  targetClassNamesToSelectors,
  TourClassesMock
} from "../../tours";
import EditableItemName from "../../components/editor/EditableItemName";
import {Challenge} from "../../schema";

export const NAME = "editorProjectEdit";

/// Targets
type TargetNames = [
  "projectName", "challengeTable", "dragHandle", "projectCustomizeForm", "language", "topRightMenu", "participants",
  "shareButton", "addChallenge", "editorMain"
]
type TargetName = TargetNames[number]
const targetNames: TargetNames = [
  "projectName", "challengeTable", "dragHandle", "projectCustomizeForm", "language", "topRightMenu", "participants",
  "shareButton", "addChallenge", "editorMain"
]

export type Targets = Record<TargetName, TourStep["target"]>

export const classes = getTargetsClassNamesWithPrefix(targetNames, "tour_editor_projectEdit_");

/// Types
type MockDynamicData = {
  projectName?: string
}

export type EditorProjectEditTourMock = {
  tour: "editorProjectEdit"

  nameEditSave: React.ComponentProps<typeof EditableItemName>["onSave"]
  onReturnToMainPage: () => void,
  data: MockDynamicData,

  getBy: {
    challenge: Map<Challenge["id"], MockByChallenge>
  }
}

export function isEditorProjectEditTourMock(mock: any): mock is EditorProjectEditTourMock {
  return mock?.tour === NAME;
}

type MockByChallenge = TourClassesMock<"dragHandle">

/// Component
type props = {
  targets?: Partial<Targets>,
  returnToPreviousPage?: boolean
}

export default function EditorProjectEditTour({targets: componentTargets, returnToPreviousPage}: props) {
  const {t} = useTranslation("tours");
  const {end: endTour, run: runTour, setMock} = useTour();

  const history = useHistory();

  const onTourEnd = useCallback(() => {
    if (returnToPreviousPage) {
      history.goBack();
      return true;
    }
    return endTour(NAME);
  }, [returnToPreviousPage, history, endTour])

  useEffect(() => () => {
    endTour(NAME);
  }, [endTour])

  const [helpers, setHelpers] = useState<StoreHelpers>();

  const [data, setData] = useState<EditorProjectEditTourMock["data"]>({})

  const targets = useMemo(() => ({
    ...targetClassNamesToSelectors(classes),
    ...componentTargets
  }), [componentTargets])

  const mock = useMemo<Omit<EditorProjectEditTourMock, "tour" | "mockBy" | "data" | "getBy">>(() => ({
    nameEditSave: ({name}) => {
      setData(prevData => ({...prevData, projectName: name}))
      setTimeout(() => helpers?.next())
    },
    onReturnToMainPage: () => {
      setTimeout(() => runTour("editorIndex_openChallenge", true))
    }
  }), [helpers, runTour])

  const challengeMocks = useMemo<Map<Challenge["id"], MockByChallenge>>(() => {
    const challenges = ids.project.challenges
    return new Map([
      [challenges[0].id, {
        classes: {
          dragHandle: classes.dragHandle,
        }
      }]
    ])
  }, [])

  useEffect(() => {
    setMock({
      ...mock,
      data,
      tour: NAME,
      getBy: {
        challenge: challengeMocks
      }
    })
    return () => setMock(undefined);
  }, [mock, data, challengeMocks, setMock])

  const steps = useMemo<TourStep[]>(() => ([
    {
      title: t("editorProjectEdit.start.title"),
      content: t("editorProjectEdit.start.content"),
      target: "body",
      placement: "center",
    },
    {
      title: t("editorProjectEdit.projectName.title"),
      content: t("editorProjectEdit.projectName.content"),
      target: targets.projectName,
      placement: "bottom",
      disableNextButton: true,
      spotlightClicks: true
    },
    {
      title: t("editorProjectEdit.projectNameUpdated.title"),
      content: t("editorProjectEdit.projectNameUpdated.content"),
      target: targets.projectName,
      placement: "bottom",
    },
    // List of challenges
    {
      title: t("editorProjectEdit.challengeTable.title"),
      content: t("editorProjectEdit.challengeTable.content"),
      target: targets.challengeTable,
      placement: "right",
    },
    {
      title: t("editorProjectEdit.dragHandle.title"),
      content: t("editorProjectEdit.dragHandle.content"),
      target: targets.dragHandle,
      placement: "bottom-start",
    },
    // Project preferences
    {
      title: t("editorProjectEdit.projectCustomizeForm.title"),
      content: t("editorProjectEdit.projectCustomizeForm.content"),
      target: targets.projectCustomizeForm,
      placement: "left",
    },
    {
      title: t("editorProjectEdit.language.title"),
      content: t("editorProjectEdit.language.content"),
      target: targets.language,
      placement: "left",
    },
    // Top right menu
    {
      title: t("editorProjectEdit.topRightMenu.title"),
      content: t("editorProjectEdit.topRightMenu.content"),
      target: targets.topRightMenu,
      placement: "bottom",
    },
    {
      title: t("editorProjectEdit.participants.title"),
      content: t("editorProjectEdit.participants.content"),
      target: targets.participants,
      placement: "bottom",
    },
    {
      title: t("editorProjectEdit.shareButton.title"),
      content: t("editorProjectEdit.shareButton.content"),
      target: targets.shareButton,
      placement: "bottom",
    },
    {
      title: t("editorProjectEdit.addChallenge.title"),
      content: t("editorProjectEdit.addChallenge.content"),
      target: targets.addChallenge,
      placement: "bottom",
    },
    // Final
    {
      title: t("editorProjectEdit.editorMain.title"),
      content: t("editorProjectEdit.editorMain.content"),
      target: targets.editorMain,
      placement: "bottom",
      spotlightClicks: true
    },
  ]), [t, targets])

  return (
    <ReactTour
      continuous
      run
      steps={steps}
      onTourEnd={onTourEnd}
      getHelpers={setHelpers}
      scrollToFirstStep
      disableOverlayClose
      disableCloseOnEsc
      showSkipButton
      hideCloseButton
    />
  );
}

/// IDs of mock data to refer
const ids = {
  project: {
    id: "editorProjectEditTour_Project_1",
    challenges: [
      {id: "editorProjectEditTour_Challenge_3"},
      {id: "editorProjectEditTour_Challenge_4"},
      {id: "editorProjectEditTour_Challenge_5"}
    ]
  }
}

export function useMockData(projectName?: string): queryData {
  const {t, i18n} = useTranslation("tours");
  const languageCode = getLanguageCode(i18n.language);

  const project = useMemo(() => (
    createDummyProject({
      name: projectName ?? t("editorProjectEdit.mockData.project.name"), // "Проект продвинутого волшебника"
      id: ids.project.id,
      languageCode,
      lastUpdatedAt: Date.UTC(2021, 8, 16, 15, 28)/1000,
      challenges: [
        createDummyChallenge({
          name: t("editorProjectEdit.mockData.project.challenges.1.name"), // "1. Использование волшебной палочки"
          id: ids.project.challenges[0].id,
          languageCode,
          lastUpdatedAt: Date.UTC(2021, 8, 16, 15, 30)/1000
        }),
        createDummyChallenge({
          name: t("editorProjectEdit.mockData.project.challenges.2.name"), // "2. Базовые заклинания"
          id: ids.project.challenges[1].id,
          languageCode,
          lastUpdatedAt: Date.UTC(2021, 8, 16, 15, 42)/1000
        }),
        createDummyChallenge({
          name: t("editorProjectEdit.mockData.project.challenges.3.name"), // "3. Зельеварение"
          id: ids.project.challenges[2].id,
          languageCode,
          lastUpdatedAt: Date.UTC(2021, 8, 16, 15, 55)/1000
        })
      ]
    })
  ), [projectName, languageCode, t]);
  return {
    project,
    projects: []
  };
}
